import {ReactElement} from "react";
import {ICardStrategy} from "../card.strategy.interface";
import {
    IEmpCardStrategyViewModel
} from "./emp-card-strategy.view-model.interface";
import {Price} from "../../../../../../../currency/price";
import {BookingModel} from "../../../../../booking.model";
import {
    EmpCardPaymentMethodsTabContentComponent
} from "../../../../../../../../pages/booking-flow/payment/card/emp/emp-card-payment-methods-tab-content.component";
import {FormModel} from "../../../../../../../../models/forms/form.model";
import {FormFields} from "../../../../../../../../models/forms/form-field.interface";
import {IPaymentCardDetails} from "../../../../../../../airline-webapi/requests/begin-booking-payment.request";
import {IEmpConfiguration} from "../../../../../../../configuration/objects/psp-configuration.interface";
import {ICardNetworkViewModel} from "../../networks/card-network.view-model.interface";
import {reaction} from "mobx";
import {CardExpiration} from "../../../../../../../../types/card-expiration";
import {IStandardCardFields} from "../../standard-card-fields.interface";
import {
    CardNumberValidator,
    DenyAmexValidator
} from "../../../../../../../../models/forms/field-validators/card-number-validator";
import {Check} from "../../../../../../../../types/type-checking";


export class EmpCardStrategy extends FormModel<IStandardCardFields> implements ICardStrategy, IEmpCardStrategyViewModel {
    constructor(private readonly booking: BookingModel) {
        super(booking.services);

        reaction(() => booking.paymentHandler.paymentMethodsDisabled, () => {
            this.resetErrorsValidation();
        });
    }

    private _getValueForDev<TValue>(value: TValue) : TValue | null {
        if(this.services.configuration.isDevEnv) {
            return value;
        }
        return null;
    }

    protected _createFields(): FormFields<IStandardCardFields> {
        return {
            cardHolder: this._createField<string>({
                fieldName: () => this.services.language.translate('Card holder'),
                defaultValue: this._getValueForDev("Florin iacob"),
                maxLength: 255,
                isReadOnly: () => this.booking.paymentHandler.paymentMethodsDisabled
            }),
            cardNumber: this._createField<string>({
                fieldName: () => this.services.language.translate('Card number'),
                defaultValue: this._getValueForDev("4938730000000001"),
                isReadOnly: () => this.booking.paymentHandler.paymentMethodsDisabled,
                validators: [
                    new CardNumberValidator(this.services),
                    new DenyAmexValidator(this.services)
                ]

            }),
            expirationDate: this._createField<CardExpiration>({
                fieldName: () => this.services.language.translate('Expiration date'),
                defaultValue:  this._getValueForDev(new CardExpiration(6, 2029)),
                isReadOnly: () => this.booking.paymentHandler.paymentMethodsDisabled,
                validate: (field) => {
                    const fieldValue = field.value;
                    if(!fieldValue) {
                        return null;
                    }

                    if(fieldValue.isInThePast()) {
                        return this.services.language.translate('Expiration date must be in the future');
                    }

                    return null;
                }
            }),
            cvv: this._createField<string>({
                fieldName: () => this.services.language.translate('Security code'),
                defaultValue: this._getValueForDev("123"),
                isReadOnly: () => this.booking.paymentHandler.paymentMethodsDisabled,
                validate: (field) => {
                    const fieldValue = field.value?.trim();
                    if(!fieldValue) {
                        return null;
                    }

                    for(let i = 0; i < fieldValue.length; i++) {
                        const digit = parseInt(fieldValue[i]);
                        if(!Check.isNumber(digit)) {
                            return this.services.language.translate('Invalid CVV');
                        }

                    }

                    return null;
                }
            })
        };

    }

    get amount(): Price {
        return this.booking.balanceDue;
    }

    get config(): IEmpConfiguration {
        return this.services.configuration.data.pspConfigs.EMP;
    }

    private get publicKey(): string {
        return this.config.clientEncryptionPublicKey;
    }

    get cardNetworks(): ICardNetworkViewModel[] {
        return this.booking.paymentHandler.cardPaymentMethod.networks;
    }

    renderTabContent(): ReactElement{
        return (
            <EmpCardPaymentMethodsTabContentComponent empStrategy={this}/>
        )
    }

    async startPayment(): Promise<void> {

        if(this.activateErrorsValidation().length > 0) {
            return;
        }

        let cardDetails: IPaymentCardDetails = {
            cardHolder: this.fields.cardHolder.value!.trim(),
            cardNumber: this.fields.cardNumber.value!.trim(),
            expirationMonth: this.fields.expirationDate.value?.month.toString() ?? "",
            expirationYear: this.fields.expirationDate.value?.year.toString() ?? "",
            cvv: this.fields.cvv.value!.toString(),
        }

        const empEncrypto = window.Encrypto.createEncryption(this.publicKey);


        await this.booking.paymentHandler.startBookingPayment({
            paymentMethodCode: this.booking.paymentHandler.cardPaymentMethod.code,
            cardDetails: empEncrypto.encrypt(cardDetails),
            useIframeForRedirect: this.config.useRedirectInIframe === "true"
        });
    }


}


/*
    private readonly publicKey = 'LS0tLS1CRUdJTiBQVUJMSUMgS0VZLS0tLS0KTUlJQklqQU5CZ2txaGtpRzl3\n' +
        'MEJBUUVGQUFPQ0FROEFNSUlCQ2dLQ0FRRUE3Zy8vNzZ2UTBxMFVQZUNYZ2hq\n' +
        'bApHQ3BCSWdKYU9kaGdPWldjSDVNRjJpQnRNRXFlMXRxOWF2bG5oNkY2OTll\n' +
        'a24zREJpS1cvT3dpbDFQVlRkaEtLCk1HVnUwaWxTRnJLZlR1ekVDZEVRcmJJ\n' +
        'R08vRTJudEhydU1QNHA0ZE8vKzdJZktNUkJMYUtpLzZ1aHFYMDhCVk4KenZs\n' +
        'SlIzTXhoYkxhM05KazNwaVFWYjA0NUxCdWhFaVR5ZG4ySGRKUEpjVHd2cDFp\n' +
        'N3RmWllLRzJNVEJCZHNwaAowbXRCajF3RXNyZGtqYWtDYkpjS1NtS0QzY3kz\n' +
        'S1o5dFJVUXZva0UxdksrT1dmOUMyVkNWVVpHMVJoMG94VlAwCktlenozVFQ0\n' +
        'SDlCcHMxMEJXcURPQmFra3BXU2VWTmw2NDRVUG84U25uYU96UEEyWngyRHEw\n' +
        'VFJEV1l6QmUxcG0KV1FJREFRQUIKLS0tLS1FTkQgUFVCTElDIEtFWS0tLS0t\n' +
        'Cg==';
*/
